import { SearchTextField, Table, toast } from '@montugroup/design-system';
import { Box, styled } from '@mui/material';
import React, { useMemo, useState } from 'react';

import { useTableUtils } from '@/components/tables/hooks/useTableUtils';
import {
  FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT,
  FF_C4C_VIDEO_CONSULT,
  FF_ENABLE_ALTERNATE_PENDING_CANCELLATION,
} from '@/constants/featureFlags';
import { getAuthData } from '@/data/service/authService';
import { useFeatureFlags } from '@/hooks';
import { useGetDoctorCalendarConsultations } from '@/hooks/consult/useGetDoctorCalendarConsultations';
import { usePatientPhoneNumber } from '@/hooks/usePatientPhoneNumber';
import type { DoctorConsultation, ISODateString } from '@/types';
import { ConsultationTableColumnHeaders } from '@/types';
import { PatientConsultationDetailsCell } from './components/PatientConsultationDetailsCell/PatientConsultationDetailsCell';
import {
  createCancelLink,
  createRescheduleLink,
  createViewLink,
  formatPatientDob,
  formatPatientName,
  formatStartTime,
  sortByStartTime,
  sortByWaitingRoomStatus,
} from './components/SharedValuesCalendarColumns';

const StyledBox = styled(Box)({
  marginTop: 10,
  width: '100%',
});

const StyledTable = styled(Table)({
  '.MuiTableCell-root': {
    fontSize: '1rem',
  },
});

type ConsultationTabOverviewTableProps = {
  getQueryParams: {
    selectedDoctor: number | string;
    dateIsoString: ISODateString;
  };
};

/**
 * DoctorCalendarTable component
 *
 * @remarks
 * Renders a table of consultations for doctors on the Doctor Calendar page
 *
 * @typedef {object} GetQueryParams
 * @property {string} doctor - The doctor ID
 * @property {string} dateISOString - The ISODateString of the consultations
 *
 * @param {GetQueryParams} getQueryParams - Contains 'doctor' and 'date' used as params for the GET query
 *
 * @returns {JSX.Element} JSXElement
 */

export const DoctorCalendarTable = ({ getQueryParams }: ConsultationTabOverviewTableProps) => {
  const { flags } = useFeatureFlags();
  const { shouldDisplayPatientPhoneNumber } = usePatientPhoneNumber();
  const ffAlterativePendingFlow = flags[FF_ENABLE_ALTERNATE_PENDING_CANCELLATION];
  const ffVideoConsult = flags[FF_C4C_VIDEO_CONSULT];
  const ffEnableConnectVideoConsult = flags[FF_C4C_ENABLE_CONNECT_VIDEO_CONSULT];

  const [searchFilterText, setSearchFilterText] = useState('');
  const { isDoc } = getAuthData();

  const isConnectVideoConsult = ffVideoConsult && ffEnableConnectVideoConsult;

  const { createColumnDefinition } = useTableUtils();

  const {
    doctorCalendarConsultations,
    isError: isErrorDoctorConsultations,
    isLoading: isLoadingDoctorConsultations,
    refetch: refetchGetDoctorsConsultations,
  } = useGetDoctorCalendarConsultations({
    selectedDoctor: getQueryParams.selectedDoctor,
    dateIsoString: getQueryParams.dateIsoString,
  });

  const resultsNotFoundMessage = searchFilterText.length
    ? 'No matching consultations found for that search'
    : 'No consultations found';

  if (isErrorDoctorConsultations) {
    toast.error('There has been an error loading the Doctor Consultations');
  }

  const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchFilterText(event.target.value);
  };

  const handleClearSearchFilter = () => {
    setSearchFilterText('');
  };

  const formattedDoctorConsultations = doctorCalendarConsultations?.map((consultation: DoctorConsultation) => {
    const patientPhoneNumber = shouldDisplayPatientPhoneNumber ? { patient_phone: consultation.patient_phone } : {};

    return {
      id: consultation.consultation_code,
      start_time: consultation.start_time,
      waiting_room_status: consultation.waiting_room_status,
      appointment_time: formatStartTime(consultation),
      practitioner: consultation.doctor_name,
      patient_name: formatPatientName(consultation),
      ...patientPhoneNumber,
      patient_email: consultation.patient_email,
      patient_code: consultation.patient_code,
      patient_dob: formatPatientDob(consultation),
      consultation_type: `${consultation.consult_type} - Doctor`,
      consultation_status: consultation.status,
      view: createViewLink(consultation),
      detail: <PatientConsultationDetailsCell consultation={consultation} />,
      reschedule: createRescheduleLink(consultation, refetchGetDoctorsConsultations),
      cancel: createCancelLink(consultation, ffAlterativePendingFlow, refetchGetDoctorsConsultations),
    };
  });

  const columns = useMemo(() => {
    if (isConnectVideoConsult) {
      return [
        createColumnDefinition('detail', ConsultationTableColumnHeaders.Details, undefined, {
          sortingFn: sortByWaitingRoomStatus,
        }),
        createColumnDefinition('id', ConsultationTableColumnHeaders.ConsultID),
        createColumnDefinition('appointment_time', ConsultationTableColumnHeaders.AppointmentTime, undefined, {
          sortingFn: sortByStartTime,
        }),
        createColumnDefinition('patient_name', ConsultationTableColumnHeaders.PatientName),
        createColumnDefinition('patient_code', ConsultationTableColumnHeaders.PatientId),
        createColumnDefinition('patient_dob', ConsultationTableColumnHeaders.PatientDob),
        createColumnDefinition('patient_phone', ConsultationTableColumnHeaders.PatientPhone),
        createColumnDefinition('patient_email', ConsultationTableColumnHeaders.PatientEmail),
        createColumnDefinition('practitioner', ConsultationTableColumnHeaders.Practitioner),
        createColumnDefinition('consultation_type', ConsultationTableColumnHeaders.ConsultationType),
        createColumnDefinition('consultation_status', ConsultationTableColumnHeaders.ConsultationStatus),
        createColumnDefinition('reschedule', ConsultationTableColumnHeaders.RescheduleLink),
        createColumnDefinition('cancel', ConsultationTableColumnHeaders.CancelLink),
      ];
    }

    return [
      createColumnDefinition('view', ConsultationTableColumnHeaders.ViewLink),
      createColumnDefinition('id', ConsultationTableColumnHeaders.ConsultID),
      createColumnDefinition('appointment_time', ConsultationTableColumnHeaders.AppointmentTime, undefined, {
        sortingFn: sortByStartTime,
      }),
      createColumnDefinition('practitioner', ConsultationTableColumnHeaders.Practitioner),
      createColumnDefinition('patient_name', ConsultationTableColumnHeaders.PatientName),
      createColumnDefinition('patient_phone', ConsultationTableColumnHeaders.PatientPhone),
      createColumnDefinition('patient_email', ConsultationTableColumnHeaders.PatientEmail),
      createColumnDefinition('patient_code', ConsultationTableColumnHeaders.PatientId),
      createColumnDefinition('patient_dob', ConsultationTableColumnHeaders.PatientDob),
      createColumnDefinition('consultation_type', ConsultationTableColumnHeaders.ConsultationType),
      createColumnDefinition('consultation_status', ConsultationTableColumnHeaders.ConsultationStatus),
      createColumnDefinition('reschedule', ConsultationTableColumnHeaders.RescheduleLink),
      createColumnDefinition('cancel', ConsultationTableColumnHeaders.CancelLink),
    ];
  }, [createColumnDefinition, isConnectVideoConsult]);

  return (
    <StyledBox>
      <SearchTextField
        handleChange={handleSearchOnChange}
        handleClear={handleClearSearchFilter}
        labelText="Search"
        enableClear={true}
      />
      <StyledTable
        data={formattedDoctorConsultations || []}
        columns={columns}
        initialSort={[{ id: 'appointment_time', desc: false }]}
        isLoading={isLoadingDoctorConsultations}
        pageSize={20}
        globalFilter={searchFilterText}
        noDataFoundMessage={resultsNotFoundMessage}
        columnVisibility={{
          patient_phone: shouldDisplayPatientPhoneNumber,
          reschedule: !isDoc, // 'Reschedule' is hidden to doctors
          cancel: !isDoc, // 'Cancel' is hidden to doctors
          practitioner: !isDoc, // 'Practitioner' is hidden to doctors
        }}
        showPagination
      />
    </StyledBox>
  );
};
